import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Filter, CreditCard, Wallet, TrendingUp, Lightbulb, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormField } from '@/components/common/FormSection';
import { AmountText, FinanceStatusBadge } from '@/components/finance-v2/FinanceBadges';
import type { Account, AccountType } from '../types/financeV2.types';
import { financeV2Service, type CreateAccountDto, type AccountFilters } from '../services/financeV2Service';
import { getErrorMessage } from '../utils/error.utils';
import { parseDbTimestamp } from '../utils/datetime';

export function FinanceV2AccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AccountFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.accounts.list({ ...filters, page, limit: 20 });
      setAccounts(response.data.accounts);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar contas'));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR') ?? '-';
  };

  const getAccountTypeLabel = (type: AccountType) => {
    const labels: Record<AccountType, string> = {
      BANK_ACCOUNT: 'Conta Bancária',
      CASH: 'Dinheiro',
      INVESTMENT: 'Investimento',
      CREDIT_CARD: 'Cartão de Crédito',
      DIGITAL_WALLET: 'Carteira Digital',
      OTHER: 'Outro'
    };
    return labels[type];
  };

  const getAccountIcon = (type: AccountType) => {
    const Icon = type === 'CASH' ? Wallet : type === 'INVESTMENT' ? TrendingUp : CreditCard;
    return <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />;
  };

  const getStatusBadge = (isActive: boolean) => (
    isActive
      ? <FinanceStatusBadge status="Ativa" />
      : <Badge variant="outline" className="text-muted-foreground">Inativa</Badge>
  );

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Wallet className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Contas</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie suas contas bancárias e outros ativos financeiros</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Nova Conta
        </Button>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="text"
            aria-label="Buscar contas"
            placeholder="Buscar contas..."
            className="pl-8"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
          <Filter className="size-4" /> Filtros
        </Button>
      </div>

      {showFilters && (
        <Card size="sm">
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            <FormField label="Tipo" htmlFor="accounts-filter-type">
              <NativeSelect
                id="accounts-filter-type"
                value={filters.type || ''}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as AccountType || undefined })}
              >
                <option value="">Todos</option>
                <option value="BANK_ACCOUNT">Conta Bancária</option>
                <option value="CASH">Dinheiro</option>
                <option value="INVESTMENT">Investimento</option>
                <option value="CREDIT_CARD">Cartão de Crédito</option>
                <option value="DIGITAL_WALLET">Carteira Digital</option>
                <option value="OTHER">Outro</option>
              </NativeSelect>
            </FormField>

            <FormField label="Status" htmlFor="accounts-filter-status">
              <NativeSelect
                id="accounts-filter-status"
                value={filters.isActive === undefined ? '' : filters.isActive.toString()}
                onChange={(e) => setFilters({ ...filters, isActive: e.target.value === '' ? undefined : e.target.value === 'true' })}
              >
                <option value="">Todos</option>
                <option value="true">Ativas</option>
                <option value="false">Inativas</option>
              </NativeSelect>
            </FormField>

            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando contas...</span>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Wallet className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhuma conta encontrada</h3>
            <p className="text-sm text-muted-foreground">Comece criando uma nova conta.</p>
            <Button className="mt-2" onClick={() => setShowCreateModal(true)}>
              <Plus className="size-4" /> Nova Conta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Conta</TableHead>
                  <TableHead>Titular</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Saldo Atual</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data Início</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {getAccountIcon(account.type)}
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{account.name}</div>
                          {account.bankName && (
                            <div className="text-sm text-muted-foreground">{account.bankName}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{account.holderName}</TableCell>
                    <TableCell>{getAccountTypeLabel(account.type)}</TableCell>
                    <TableCell>
                      <AmountText tone={account.currentBalance >= 0 ? 'income' : 'expense'}>
                        {formatCurrency(account.currentBalance)}
                      </AmountText>
                    </TableCell>
                    <TableCell>{getStatusBadge(account.isActive)}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {formatDate(account.trackingStartDate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
              <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  <ChevronLeft className="size-4" /> Anterior
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  Próxima <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {showCreateModal && (
        <CreateAccountModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadAccounts();
          }}
        />
      )}
    </div>
  );
}

// Create Account Modal Component
function CreateAccountModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState<CreateAccountDto>({
    name: '',
    holderName: '',
    type: 'BANK_ACCOUNT',
    initialBalance: 0,
    trackingStartDate: new Date().toISOString().split('T')[0],
    bankName: '',
    accountNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await financeV2Service.accounts.create(formData);
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar conta'));
    } finally {
      setLoading(false);
    }
  };

  const isBankAccount = formData.type === 'BANK_ACCOUNT';

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Conta</DialogTitle>
          <DialogDescription>Cadastre uma conta para registrar e acompanhar os lançamentos.</DialogDescription>
        </DialogHeader>

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            {error}
          </div>
        )}

        <form id="create-account-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label="Nome da Conta *" htmlFor="account-name">
            <Input
              id="account-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </FormField>

          <FormField label="Nome do Titular *" htmlFor="account-holder">
            <Input
              id="account-holder"
              type="text"
              required
              value={formData.holderName}
              onChange={(e) => setFormData({ ...formData, holderName: e.target.value })}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Tipo *" htmlFor="account-type">
              <NativeSelect
                id="account-type"
                required
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as AccountType })}
              >
                <option value="BANK_ACCOUNT">Conta Bancária</option>
                <option value="CASH">Dinheiro</option>
                <option value="INVESTMENT">Investimento</option>
                <option value="CREDIT_CARD">Cartão de Crédito</option>
                <option value="DIGITAL_WALLET">Carteira Digital</option>
                <option value="OTHER">Outros (Dinheiro Físico, Caixinha, etc.)</option>
              </NativeSelect>
              {formData.type === 'OTHER' && (
                <p className="flex items-start gap-2 rounded-lg border bg-muted/40 p-2 text-sm">
                  <Lightbulb className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  Ideal para rastrear dinheiro físico do escritório, cofre, caixinha ou outras contas personalizadas
                </p>
              )}
            </FormField>

            <FormField label="Saldo Inicial *" htmlFor="account-initial-balance">
              <Input
                id="account-initial-balance"
                type="number"
                step="0.01"
                required
                value={formData.initialBalance || ''}
                onChange={(e) => setFormData({ ...formData, initialBalance: parseFloat(e.target.value) || 0 })}
              />
            </FormField>
          </div>

          <FormField label="Data de Início do Rastreamento *" htmlFor="account-tracking-start">
            <Input
              id="account-tracking-start"
              type="date"
              required
              value={formData.trackingStartDate}
              onChange={(e) => setFormData({ ...formData, trackingStartDate: e.target.value })}
            />
          </FormField>

          {/* Campos bancários - apenas se não for "Outros" */}
          {formData.type !== 'OTHER' && formData.type !== 'CASH' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label={`Banco ${isBankAccount ? '*' : ''}`} htmlFor="account-bank">
                <Input
                  id="account-bank"
                  type="text"
                  required={isBankAccount}
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                />
              </FormField>

              <FormField label={`Número da Conta ${isBankAccount ? '*' : ''}`} htmlFor="account-number">
                <Input
                  id="account-number"
                  type="text"
                  required={isBankAccount}
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                />
              </FormField>
            </div>
          )}

          {/* Descrição adicional para contas "Outros" */}
          {formData.type === 'OTHER' && (
            <div className="rounded-lg border bg-muted/40 p-3">
              <h4 className="mb-2 text-sm font-medium">Exemplos de uso:</h4>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Cofre do escritório</li>
                <li>Caixinha de troco</li>
                <li>Dinheiro físico em mãos</li>
                <li>Fundo específico do escritório</li>
                <li>Qualquer outro tipo de conta personalizada</li>
              </ul>
              <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                O saldo será rastreado automaticamente conforme você registrar transações
              </p>
            </div>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-account-form" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Conta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
